import { CloseOutlined, EditOutlined, SaveOutlined } from "@ant-design/icons";
import { FilterRow } from "@components/FilterBar";
import ObjectSelect from "@components/ObjectSelect";
import { feederConnectionColumns } from "@features/communications/config/connectionColumns";
import { type Contract, useAdminContracts } from "@features/contracts/api";
import {
  type ChannelMeterRow,
  type DeviceObject,
  useChannelsFlat,
  useFeederConnections,
  useObjects,
  useUpdateFeeder,
} from "@features/devices/api";
import { type DirectoryGroup, useGroups } from "@features/groups/api";
import { PageLayoutWithHelp } from "@features/page-help";
import type { Connection } from "@shared/api/generated/model";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import { isAdminUser } from "@shared/rbac";
import EnterpriseDrawer from "@shared/ui/enterprise-drawer";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useStoredUser } from "@shared/useStoredUser";
import {
  App as AntdApp,
  Button,
  Descriptions,
  Flex,
  Form,
  Input,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMemo, useState } from "react";

const meterKey = (r: {
  region_id: number;
  object_id: number;
  feeder_id: number;
  meter_number: number;
}) => `${r.region_id}:${r.object_id}:${r.feeder_id}:${r.meter_number}`;

const DevicesPage = () => {
  const { message } = AntdApp.useApp();
  const currentUser = useStoredUser();
  const isAdmin = isAdminUser(currentUser);
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject, setSelectedObject } = useGlobalObject(objects);
  const [meterSearch, setMeterSearch] = useState("");
  const updateFeeder = useUpdateFeeder();
  const contractsQuery = useAdminContracts(undefined, isAdmin);

  // Lookup: meterKey → contract (для бейджей в таблице)
  const contractByMeterKey = useMemo(() => {
    const map = new Map<string, Contract>();
    for (const c of contractsQuery.data ?? []) {
      for (const m of c.meters ?? []) {
        map.set(`${m.region_id}:${m.object_id}:${m.feeder_id}:${m.meter_number}`, c);
      }
    }
    return map;
  }, [contractsQuery.data]);

  const channelsQuery = useChannelsFlat(selectedObject?.objectId, meterSearch.trim());

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<ChannelMeterRow | null>(null);
  const groupsQuery = useGroups(
    editingRow
      ? { object: { regionId: editingRow.region_id, objectId: editingRow.object_id } }
      : undefined,
  );
  const feederConnectionsQuery = useFeederConnections(
    editingRow?.region_id,
    editingRow?.object_id,
    editingRow?.feeder_id,
  );
  const feederConnections = feederConnectionsQuery.data ?? [];
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [form] = Form.useForm();

  const openEdit = (row: ChannelMeterRow) => {
    setEditingRow(row);
    form.setFieldsValue({
      feeder_name: row.feeder_name ?? "",
      cod_kre: row.cod_kre ?? "",
    });
    setDrawerOpen(true);
  };

  const handleClose = () => {
    setDrawerOpen(false);
    setIsEditing(false);
    setIsDirty(false);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    if (!editingRow) return;
    updateFeeder.mutate(
      {
        regionId: editingRow.region_id,
        objectId: editingRow.object_id,
        feederID: editingRow.feeder_id,
        data: {
          feeder_name: values.feeder_name?.trim() || null,
          cod_kre: values.cod_kre?.trim() || null,
        },
      },
      {
        onSuccess: () => {
          message.success("Сохранено");
          setDrawerOpen(false);
          channelsQuery.refetch();
        },
        onError: (err) => message.error(extractErrorMessage(err)),
      },
    );
  };

  const objectsMap = useMemo(() => {
    const map = new Map<string, DeviceObject>();
    for (const obj of objects) map.set(`${obj.syb_rnk}:${obj.n_ob}`, obj);
    return map;
  }, [objects]);

  const formatRatio = (primary?: number | null, secondary?: number | null) => {
    if (!primary || !secondary) return "—";
    return `${primary} / ${secondary}`;
  };

  const formatNumber = (value?: number | null) => (value == null ? "—" : value);

  const columns: ColumnsType<ChannelMeterRow> = [
    { title: "ID", dataIndex: "object_id", width: 60 },
    {
      title: "Объект",
      dataIndex: "object_id",
      width: 180,
      ellipsis: { showTitle: false },
      render: (objectID: number, row) => {
        const object = objectsMap.get(`${row.region_id}:${objectID}`);
        const name = object?.txt ?? "—";
        return <Tooltip title={name}>{name}</Tooltip>;
      },
    },
    { title: "Шина", dataIndex: "bus_number", width: 80 },
    { title: "Класс напр.", dataIndex: "voltage_class", width: 100 },
    { title: "№ фидера", dataIndex: "feeder_id", width: 80 },
    {
      title: "Фидер",
      dataIndex: "feeder_name",
      width: 180,
      ellipsis: { showTitle: false },
      render: (v?: string) => <Tooltip title={v}>{v ?? "—"}</Tooltip>,
    },
    {
      title: "Диспетч. название",
      dataIndex: "dispatcher_name",
      width: 180,
      ellipsis: { showTitle: false },
      render: (v?: string) => <Tooltip title={v}>{v ?? "—"}</Tooltip>,
    },
    { title: "Счётчик №", dataIndex: "meter_number", width: 120 },
    {
      title: "Название",
      dataIndex: "channel_label",
      width: 180,
      ellipsis: { showTitle: false },
      render: (value: string | undefined, row) => {
        const label = value ?? row.meter_number;
        return <Tooltip title={label}>{label}</Tooltip>;
      },
    },
    {
      title: "Тип счётчика",
      dataIndex: "meter_type",
      width: 140,
      ellipsis: { showTitle: false },
      render: (v?: string) => <Tooltip title={v}>{v ?? "—"}</Tooltip>,
    },
    {
      title: "Кт(1)/Кт(2)",
      dataIndex: "feeder_kt_primary",
      width: 150,
      render: (_value, row) => formatRatio(row.feeder_kt_primary, row.feeder_kt_secondary),
    },
    {
      title: "Кн(1)/Кн(2)",
      dataIndex: "feeder_kn_primary",
      width: 150,
      render: (_value, row) => formatRatio(row.feeder_kn_primary, row.feeder_kn_secondary),
    },
    { title: "Кт", dataIndex: "channel_kt", width: 120, render: formatNumber },
    { title: "Кн", dataIndex: "channel_kn", width: 120, render: formatNumber },
    { title: "М", dataIndex: "multiplier", width: 100, render: formatNumber },
    { title: "Интервал, мин", dataIndex: "interval_minutes", width: 140, render: formatNumber },
    { title: "Измерения", dataIndex: "measurements", ellipsis: true },
    { title: "Код ТУ", dataIndex: "cod_kre", width: 180, render: (v?: string) => v || "—" },
    {
      title: "Договор",
      width: 120,
      render: (_: unknown, row: ChannelMeterRow) => {
        const contract = contractByMeterKey.get(meterKey(row));
        return contract ? <Tag color="blue">{contract.contract_number}</Tag> : null;
      },
    },
  ];

  // Договор, привязанный к выбранному счётчику (из общего lookup)
  const linkedContract = editingRow ? (contractByMeterKey.get(meterKey(editingRow)) ?? null) : null;

  const linkedGroups = useMemo(() => {
    if (!editingRow || !groupsQuery.data) return [];
    return groupsQuery.data.filter((g) =>
      g.channels?.some(
        (ch) =>
          ch.object_id === editingRow.object_id &&
          ch.region_id === editingRow.region_id &&
          ch.feeder_id === editingRow.feeder_id,
      ),
    );
  }, [editingRow, groupsQuery.data]);

  const groupColumns: ColumnsType<DirectoryGroup> = [
    { title: "Название", dataIndex: "name", ellipsis: true },
    {
      title: "Уровень",
      dataIndex: "level",
      width: 90,
      render: (v: number) => <Tag>{v === 1 ? "L1" : "L2"}</Tag>,
    },
    {
      title: "Интервал",
      dataIndex: "interval_minutes",
      width: 90,
      render: (v?: number) => (v ? `${v} мин` : "—"),
    },
    {
      title: "Каналы",
      dataIndex: "channels",
      width: 80,
      render: (_v: unknown, record: DirectoryGroup) => record.channels?.length ?? 0,
    },
  ];

  const channels = channelsQuery.data ?? [];

  return (
    <PageLayoutWithHelp title="Счётчики и каналы">
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <FilterRow>
          <ObjectSelect
            objects={objects}
            loading={objectsQuery.isLoading}
            value={selectedObject}
            onChange={(value) => setSelectedObject(value)}
            allowClear
            placeholder="Фильтр по объекту"
            style={{ minWidth: 260 }}
          />
          <Input
            placeholder="Поиск по счётчику"
            style={{ width: 280 }}
            value={meterSearch}
            onChange={(e) => setMeterSearch(e.target.value)}
            allowClear
          />
        </FilterRow>
        <EnterpriseTable<ChannelMeterRow>
          rowKey={(record) => `${record.object_id}-${record.feeder_id}-${record.meter_number}`}
          columns={columns}
          dataSource={channels}
          loading={channelsQuery.isLoading}
          scroll={{ x: 1000 }}
          size="small"
          toolbar={{ reload: true, onReload: () => channelsQuery.refetch() }}
          onRow={(record) => ({
            onClick: () => openEdit(record),
            style: { cursor: "pointer" },
          })}
        />
      </Space>

      <EnterpriseDrawer
        title={editingRow ? `Фидер #${editingRow.feeder_id}` : "Фидер"}
        open={drawerOpen}
        onClose={handleClose}
        size="large"
        destroyOnHidden
        actions={
          <Flex gap={8}>
            <Tooltip title="Отменить">
              <Button icon={<CloseOutlined />} onClick={handleClose} />
            </Tooltip>
            {!isEditing ? (
              <Tooltip title="Редактировать">
                <Button icon={<EditOutlined />} onClick={() => setIsEditing(true)} />
              </Tooltip>
            ) : (
              <Tooltip title="Сохранить">
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSubmit}
                  loading={updateFeeder.isPending}
                  disabled={!isDirty}
                />
              </Tooltip>
            )}
          </Flex>
        }
      >
        {editingRow && (
          <Tabs
            defaultActiveKey="params"
            items={[
              {
                key: "params",
                label: "Параметры",
                children: (
                  <Space orientation="vertical" size="large" style={{ width: "100%" }}>
                    <Form form={form} layout="vertical" onValuesChange={() => setIsDirty(true)}>
                      <Form.Item name="feeder_name" label="Название фидера">
                        <Input placeholder="Название" disabled={!isEditing} />
                      </Form.Item>
                      <Form.Item name="cod_kre" label="Код точки учёта (80020)">
                        <Input placeholder="Код ТУ" disabled={!isEditing} />
                      </Form.Item>
                    </Form>

                    <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
                      <Descriptions.Item label="Объект">#{editingRow.object_id}</Descriptions.Item>
                      <Descriptions.Item label="Регион">{editingRow.region_id}</Descriptions.Item>
                      <Descriptions.Item label="Шина">
                        {editingRow.bus_number ?? "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Класс напряжения">
                        {editingRow.voltage_class ?? "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="№ фидера">{editingRow.feeder_id}</Descriptions.Item>
                      <Descriptions.Item label="Диспетч. название">
                        {editingRow.dispatcher_name ?? "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Счётчик №">
                        {editingRow.meter_number ?? "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Тип счётчика">
                        {editingRow.meter_type ?? "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Кт(1)/Кт(2)">
                        {formatRatio(editingRow.feeder_kt_primary, editingRow.feeder_kt_secondary)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Кн(1)/Кн(2)">
                        {formatRatio(editingRow.feeder_kn_primary, editingRow.feeder_kn_secondary)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Кт">
                        {formatNumber(editingRow.channel_kt)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Кн">
                        {formatNumber(editingRow.channel_kn)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Множитель">
                        {formatNumber(editingRow.multiplier)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Интервал, мин">
                        {formatNumber(editingRow.interval_minutes)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Измерения" span={2}>
                        {editingRow.measurements ?? "—"}
                      </Descriptions.Item>
                    </Descriptions>
                  </Space>
                ),
              },
              {
                key: "groups",
                label: `Группы (${linkedGroups.length})`,
                children: (
                  <EnterpriseTable<DirectoryGroup>
                    rowKey="id"
                    columns={groupColumns}
                    dataSource={linkedGroups}
                    loading={groupsQuery.isLoading}
                    size="small"
                    scroll={{ x: 400 }}
                    pagination={false}
                  />
                ),
              },
              {
                key: "contract",
                label: linkedContract
                  ? `Договор (${linkedContract.contract_number})`
                  : "Договор (—)",
                children: linkedContract ? (
                  <EnterpriseTable<Contract>
                    rowKey="id"
                    columns={[
                      { title: "ИНН", dataIndex: "inn", width: 140 },
                      { title: "Номер", dataIndex: "contract_number", width: 160 },
                      { title: "Компания", dataIndex: "company_name", ellipsis: true },
                      {
                        title: "Счётчики",
                        width: 90,
                        render: (_: unknown, r: Contract) => r.meters?.length ?? 0,
                      },
                    ]}
                    dataSource={[linkedContract]}
                    size="small"
                    scroll={{ x: 400 }}
                    pagination={false}
                  />
                ) : (
                  <Typography.Text type="secondary">Счётчик не привязан к договору</Typography.Text>
                ),
              },
              {
                key: "connections",
                label: `Соединения (${feederConnections.length})`,
                children:
                  feederConnections.length > 0 ? (
                    <EnterpriseTable<Connection>
                      rowKey={(r) => String(r.id)}
                      columns={feederConnectionColumns}
                      dataSource={feederConnections}
                      loading={feederConnectionsQuery.isLoading}
                      size="small"
                      scroll={{ x: 500 }}
                      pagination={false}
                    />
                  ) : (
                    <Typography.Text type="secondary">Нет связанных соединений</Typography.Text>
                  ),
              },
            ]}
          />
        )}
      </EnterpriseDrawer>
    </PageLayoutWithHelp>
  );
};

export default DevicesPage;
